/**
 * Funnel telemetry of the New Connection dialog, version 2 (Option C, C1, plan §3.7). `funnel_version: 2` goes on
 * every funnel event (these, the entry point, test result and saved events) so v1 (the one-form dialog) and v2 split
 * cleanly. Properties are low-cardinality enums only.
 *
 * New-connection mode only — edits are not part of the activation funnel:
 * - `ui:connection_dialog_opened` once per dialog { no_connections, initial_view, funnel_version };
 * - `feature:connection_engine_filter` on the first filter keystroke { has_results };
 * - `ui:connection_engine_selected` when a card, the filter's Enter, a rail item or the compact select changes the
 *   engine { engine, source, view } (a paste's own events arrive in C3);
 * - `ui:connection_engine_back` on All engines / Escape in the form { engine, via };
 * - `ui:connection_dialog_abandoned` at most once, never after a save or a completion by import
 *   { engine (the form's engine, else the last one chosen, else 'none'), had_input, stage, imported_in_session,
 *   funnel_version }. `had_input` separates "bounced off" from "typed and gave up": some draft — on screen or cached for
 *   another engine — differs from the form at open (after a prefill handoff, whose effect runs first), so a pick alone
 *   or an untouched prefill is not input. `imported_in_session` (C3): an import, Azure review or Docker add on top of
 *   the dialog saved connections while a draft had input, so the dialog stayed open.
 * - `ui:connection_dialog_completed_by_import` (C3) when such a flow saved connections while NO draft had input: the
 *   dialog closes itself, and that is no abandonment { via: 'import' | 'azure_review' | 'docker', funnel_version }.
 */
import { useEffect, useRef } from 'react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import type { ConnectionForm } from '../state/connectionForm';
import { isManualPick, type DialogView, type EngineSource } from '../state/dialogView';

export const FUNNEL_VERSION = 2;

type Options = {
  form: ConnectionForm;
  isEditMode: boolean;
  noConnections: boolean;
  initialView: DialogView['kind'];
  /** Some draft (live or cached) or a shared field differs from the form at open (`useEngineDrafts.changedSince`). */
  changedSince: (baseline: ConnectionFormValues) => boolean;
  /**
   * The engine whose form is on screen, or null on the start view (no engine attached). Every engine change — a pick, a
   * paste or prefill patch, an Azure pick — lands here, so the abandonment is attributed to it (C1 review C1-03).
   */
  attachedEngine: () => DatabaseEngine | null;
  track: TrackFunction;
};

export function useConnectionDialogFunnel({ form, isEditMode, noConnections, initialView, changedSince, attachedEngine, track }: Options) {
  const savedRef = useRef(false);
  const settledRef = useRef(false);
  const importedRef = useRef(false);
  const filteredRef = useRef(false);
  const baselineRef = useRef<ConnectionFormValues | null>(null);
  // The engine the funnel attributes to: none until one is chosen (a prefill or `initialEngine` chooses one at open).
  const engineRef = useRef<DatabaseEngine | 'none'>('none');
  useEffect(() => {
    if (isEditMode) return;
    baselineRef.current = { ...form.state.values };
    if (initialView === 'form') engineRef.current = form.state.values.engine;
    track('ui:connection_dialog_opened', { no_connections: noConnections, initial_view: initialView, funnel_version: FUNNEL_VERSION });
    // Once per dialog open (mount), not per prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const funnelOnly = (fire: () => void) => {
    if (!isEditMode) fire();
  };

  return {
    /** A save succeeded: closing afterwards is no abandonment. */
    markSaved: () => { savedRef.current = true; },
    /** A nested flow saved connections and the dialog stayed open (a draft had input): the abandonment says so. */
    markImported: () => { importedRef.current = true; },
    /** A nested flow saved connections and no draft had input: the dialog closes itself — a completion, not a give-up. */
    completedByImport: (via: 'import' | 'azure_review' | 'docker') => funnelOnly(() => {
      if (savedRef.current || settledRef.current) return;
      settledRef.current = true;
      track('ui:connection_dialog_completed_by_import', { via, funnel_version: FUNNEL_VERSION });
    }),
    /** The engine the funnel attributes to: the last one chosen, or 'none' (C3: whether a paste changed the engine). */
    lastEngine: (): DatabaseEngine | 'none' => engineRef.current,
    filtered: (hasResults: boolean) => funnelOnly(() => {
      if (filteredRef.current) return;
      filteredRef.current = true;
      track('feature:connection_engine_filter', { has_results: hasResults, funnel_version: FUNNEL_VERSION });
    }),
    engineSelected: (engine: DatabaseEngine, source: EngineSource, view: DialogView['kind']) => funnelOnly(() => {
      engineRef.current = engine;
      if (isManualPick(source)) track('ui:connection_engine_selected', { engine, source, view, funnel_version: FUNNEL_VERSION });
    }),
    engineBack: (engine: DatabaseEngine, via: 'button' | 'escape') => funnelOnly(() => {
      engineRef.current = engine;
      track('ui:connection_engine_back', { engine, via, funnel_version: FUNNEL_VERSION });
    }),
    /** On dismissal: the abandonment event, at most once, never after a save. */
    trackAbandonment: (stage: DialogView['kind']) => {
      if (isEditMode || savedRef.current || settledRef.current) return;
      settledRef.current = true;
      engineRef.current = attachedEngine() ?? engineRef.current;
      track('ui:connection_dialog_abandoned', {
        engine: engineRef.current,
        had_input: changedSince(baselineRef.current ?? form.state.values),
        stage,
        imported_in_session: importedRef.current,
        funnel_version: FUNNEL_VERSION,
      });
    },
  };
}
